<script setup lang="ts">
import TableContentRowComponent from "../../components/sections/TableContentRowComponent.vue";
import { defineProps, ref, withDefaults } from "vue";
import { ProductGroup } from "../../classes/productGroup";
import { featureIsDiff } from "../../helper/general";

interface Props {
  productGroup: ProductGroup;
  defaultProduct: number;
  highlightDefaultProduct: boolean;
  liability: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productGroup: undefined,
  defaultProduct: undefined,
  highlightDefaultProduct: false,
  liability: false,
});

const showAllFields = ref<boolean>(props.productGroup.products.length === 1);
</script>

<template>
  <div class="table-content" :class="{ liability: props.liability }">
    <template v-for="feature in props.productGroup.features" :key="feature.id">
      <table-content-row-component
        :product-group="props.productGroup"
        :feature="feature"
        :default-product="props.defaultProduct"
        :highlight-default-product="props.highlightDefaultProduct"
        :liability="props.liability"
        v-if="featureIsDiff(feature, props.productGroup)"
      ></table-content-row-component>
    </template>
    <div
      class="table-content-row show-all"
      data-cy="show-more"
      tabindex="0"
      @keyup.enter="showAllFields = !showAllFields"
      @click="showAllFields = !showAllFields"
      v-if="props.productGroup.products.length > 1"
    >
      <div class="table-content-head">
        <span>Gemeinsame Leistungen</span>
        <span
          :class="{
            'agila-icon-down': !showAllFields,
            'agila-icon-up': showAllFields,
          }"
        ></span>
      </div>
    </div>
    <template v-if="showAllFields">
      <div v-for="feature in productGroup.features" :key="feature.id">
        <table-content-row-component
          :product-group="productGroup"
          :feature="feature"
          v-if="!featureIsDiff(feature, productGroup)"
          :default-product="defaultProduct"
          :highlight-default-product="highlightDefaultProduct"
          :liability="props.liability"
        ></table-content-row-component>
      </div>
    </template>
  </div>
</template>

<style lang="scss">
@import "../../styles/variables";

.table-content {
  grid-area: d;
  @media screen and (min-width: $breakpoint-m) {
    border-top: 1px solid var(--color-gray-400);
  }

  .table-content-row {
    display: grid;
    grid-template-areas: "a" "b";
    grid-template-columns: 1fr;
    border-bottom: 1px solid var(--color-gray-400);

    @media screen and (min-width: $breakpoint-m) {
      grid-template-areas: "a b";
      grid-template-columns: minmax(350px, 34%) minmax(375px, 66%);
    }

    &.show-all {
      cursor: pointer;
      grid-template-areas: "a";
      grid-template-columns: 100%;
      font-size: 18px;
      color: var(--color-blue-standard);

      .agila-icon-up,
      .agila-icon-down {
        height: 22px;
      }

      .table-content-head {
        justify-content: space-between;
        @media screen and (min-width: $breakpoint-m) {
          padding: 16px 16px 16px 0;
        }
      }
    }

    .table-content-head,
    .table-content-column {
      display: flex;
      flex-flow: wrap;

      &__name {
        display: flex;
        align-items: center;
        width: 100%;
        line-height: 22px;
        @media screen and (max-width: $breakpoint-m - 1px) {
          justify-content: center;
          text-align: center;
        }
      }
    }

    .table-content-head {
      grid-area: a;
      padding: 8px;
      @media screen and (min-width: $breakpoint-m) {
        padding: 16px 8px 16px 0;
      }
      @media screen and (max-width: $breakpoint-m - 1px) {
        justify-content: center;
        .text-small {
          text-align: center;
        }
      }
    }

    .cursor-pointer {
      cursor: pointer;
      user-select: none;
    }

    .text-small {
      font-family: var(--font-open-sans-regular), sans-serif !important;
      font-size: 13px;
    }

    .agila-icon-info {
      margin-left: 8px;

      &:focus-visible {
        box-shadow: 0 0 0 2px var(--color-yellow-300),
          0 0 0 4px var(--color-blue-focus-line) !important;
        background-color: var(--color-yellow-300);
        border-color: var(--color-yellow-300);
        outline: none;
      }
    }
  }
}
</style>
